import { BaseRemoteService } from '@ofqual/portal-core/core/dataAccess';
import {
 Model,
 Opts,
 PagedResponse,
 PatchWithMeta,
 QueryParams,
 ValidatedResponse,
} from '@ofqual/portal-core/core/types';
import {paramsToSearchString} from '@ofqual/portal-core/core/utils/urlHelper';
import {PagedResponseTo} from '@ofqual/portal-core/core/utils/pagedResponseHelpers';
import {fromApiModel, LcmModel, LcmStatus} from '../domain/LcmModel';
import {getValidationError} from '@ofqual/portal-core/core/utils/validationHelper';
import {LcmTimeRecord} from '../domain/LcmTimeRecord';
import {PreviewResponse} from '../domain/PreviewResponse';
import {InternalMessageModel} from '../domain/InternalMessageModel';
import {CommunicationType} from "../domain/FutureEventModel";

export class LcmService extends BaseRemoteService {
 public async get(id: string, opts: Opts): Promise<LcmModel> {
  const url = `${this.apiRoot}/lcm/${id.toString()}`;
  const response = await super.getRequest<Model>(url, opts);
  return fromApiModel(response);
 }

 public async search(
  params: QueryParams,
  opts: Opts,
 ): Promise<PagedResponse<LcmModel>> {
  const url = `${this.apiRoot}/lcm/${paramsToSearchString(params)}`;
  const response = await super.getRequest<PagedResponse<Model>>(url, opts);
  return PagedResponseTo<LcmModel>(response, fromApiModel);
 }

 public async create(
  lcm: LcmModel,
  opts: Opts,
 ): Promise<ValidatedResponse<LcmModel>> {
  const url = `${this.apiRoot}/lcm`;

  try {
   const response: LcmModel = await super.postRequest(url, lcm, opts);

   return {
    isValid: true,
    data: response,
   };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async update(
  id: string,
  patchWithMeta: PatchWithMeta<LcmModel>,
  opts: Opts,
 ): Promise<ValidatedResponse<LcmModel>> {
  const url = `${this.apiRoot}/lcm/${id}`;
  try {
   const response = await super.patchRequest<LcmModel, PatchWithMeta<LcmModel>>(
    url,
    patchWithMeta,
    opts,
   );
   return {
    isValid: true,
    data: fromApiModel(response),
   };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async requestExternalCounsel(
  id: string,
  req: LcmModel,
  opts: Opts,
 ): Promise<ValidatedResponse<LcmModel>> {
  const url = `${this.apiRoot}/lcm/${id}/requestExternalCounsel`;
  try {
   const response = await super.postRequest<LcmModel>(url, req, opts);
   return {
    isValid: true,
    data: fromApiModel(response),
   };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async rejectExternalCounsel(
  lcmId: string,
  lcmExternalCounselRequestId: string,
  opts: Opts,
 ): Promise<unknown> {
  const url = `${this.apiRoot}/lcm/${lcmId}/requestForExternalCounselRejected/${lcmExternalCounselRequestId}`;
  try {
   const response = await super.postRequest(url, {}, opts);
   return { isValid: true, response };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async acceptExternalCounsel(
  lcmId: string,
  lcmExternalCounselRequestId: string,
  opts: Opts,
 ): Promise<unknown> {
  const url = `${this.apiRoot}/lcm/${lcmId}/requestForExternalCounselAccepted/${lcmExternalCounselRequestId}`;
  try {
   const response = await super.postRequest(url, {}, opts);
   return { isValid: true, response };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

    public async updateLcmStatus(
        lcmId: string,
        lcmStatus: LcmStatus,
        caseNote: string,
        opts: Opts,
    ): Promise<unknown> {
        const url = `${this.apiRoot}/lcm/${lcmId}/status/${lcmStatus}`;
        try {
            const response = await super.postRequestFormData(url, {caseNote: caseNote}, opts);
            return {isValid: true, response};
        } catch (err) {
            const validationError = getValidationError(err);
            if (validationError.isValidationError) {
                return {isValid: false, error: validationError.error};
            }
            throw err;
        }
    }

    public async createFutureEvent(
        lcmId: string,
        dateTime: Date,
        eventType: string,
        communicationType: CommunicationType,
        note: string,
        opts: Opts,
    ): Promise<unknown> {
        const url = `${this.apiRoot}/lcm/${lcmId}/create-future-event`;
        const isoDateTimeString = dateTime.toISOString();

        const futureEvent = { isoDateTimeString, eventType, communicationType, note, opts }
        try {
            const response = await super.postRequestFormData(url, futureEvent, opts);
            return {isValid: true, response};
        } catch (err) {
            const validationError = getValidationError(err);
            if (validationError.isValidationError) {
                return {isValid: false, error: validationError.error};
            }
            throw err;
        }
    }

 public async downloadAttachment(
  id: string,
  activityId: string | null | undefined,
  blobId: string | null | undefined,
  isBlob: boolean,
  opts: Opts,
 ) {
  const url = `${this.apiRoot}/Lcm/${id}/attachments/${activityId}/download/${blobId}`;
  if (!isBlob) {
   return await super.getRequest<PreviewResponse>(url, opts);
  }
  return await super.getRequestBlob(url, opts);
 }

 public async addTimeRecord(
  id: string,
  timeRecord: LcmTimeRecord,
  opts: Opts,
 ): Promise<ValidatedResponse<LcmModel>> {
  const url = `${this.apiRoot}/lcm/${id}/timeLogEvent`;
  try {
   const ticksPerDay = 864000000000;
   const ticksPerHour = 36000000000;
   const ticksPerMinute = 600000000;

   const duration =
    ticksPerDay * (timeRecord.timeDays ?? 0) +
    ticksPerHour * (timeRecord.timeHours ?? 0) +
    ticksPerMinute * (timeRecord.timeMinutes ?? 0);

   const response = await super.postRequest<LcmModel>(
    url,
    {
     LcmId: id,
     Name: timeRecord.name,
     ExternalCounsel: timeRecord.externalCounsel,
     Notes: timeRecord.notes,
     Duration: duration,
     Forecast: timeRecord.forecast,
    },
    opts,
   );
   return {
    isValid: true,
    data: fromApiModel(response),
   };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async addCaseNote(
  lcmId: string,
  caseNote: string,
  caseNoteType: number,
  meetingDate: string,
  documents: File[],
  opts: Opts,
 ): Promise<unknown> {
  const url = `${this.apiRoot}/lcm/${lcmId}/casenote`;
  try {
   const response = await super.postRequestFormData(
    url,
    { caseNote: caseNote, caseNoteType: caseNoteType, meetingDate, documents },
    opts,
   );
   return { isValid: true, response };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async addInternalMessage(
  lcmId: string,
  internalMessage: InternalMessageModel,
  opts: Opts,
 ): Promise<unknown> {
  const url = `${this.apiRoot}/lcm/${lcmId}/internal-message`;
  try {
   const serialisedMessage = JSON.stringify({
    subject: internalMessage.subject,
    message: internalMessage.message,
    recipients: internalMessage.recipients,
   });

   const response = await super.postRequestFormData(
    url,
    {
     serialisedInternalMessage: serialisedMessage,
     files: internalMessage.documents,
    },
    opts,
   );
   return { isValid: true, response };
  } catch (err) {
   const validationError = getValidationError(err);
   if (validationError.isValidationError) {
    return { isValid: false, error: validationError.error };
   }
   throw err;
  }
 }

 public async attachEmail(id: string, file: File, opts: Opts): Promise<void> {
  const url = `${this.apiRoot}/lcm/lcmAttachment/${id}/emails`;
  await super.postRequestFormData(url, { email: file }, opts);
 }

 public async downloadEmailAttachment(
  id: string,
  activityId: string,
  blobId: string,
  opts: Opts,
 ) {
  const url = `${this.apiRoot}/lcm/lcmAttachment/${id}/emails/${activityId}/attachments/${blobId}`;
  return await super.getRequestBlob(url, opts);
 }
}
